import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getItemSummaryCatalogId,
    getItemSummaryLotNumber,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';

type Props = {
    itemId: number;
};

export const useSendSellerMessage = ({ itemId }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const itemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));

    return useCallback(
        (reason: 'bidLimit' | 'registration_declined' | 'registration_issue' | 'payment_issue') => {
            let messageDetails = {};
            let subject = '';
            switch (reason) {
                case 'bidLimit':
                    subject = formatMessage({
                        id: 'bidLimitExceededMessage.subject',
                    });
                    break;

                case 'registration_declined':
                    messageDetails = {
                        catalogId,
                        message: `${formatMessage({
                            id: 'message_registration_declined',
                        })}`,
                        title: catalogTitle,
                    };

                    subject = `${formatMessage({
                        id: 'registration_declined',
                    })}: ${catalogTitle}`;
                    break;

                case 'registration_issue':
                    messageDetails = {
                        catalogId,
                        itemId,
                        lotNumber,
                        title: itemTitle,
                    };

                    subject = formatMessage({ id: 'registration.issue' }, { catalogTitle: catalogTitle });
                    break;

                case 'payment_issue':
                    messageDetails = {
                        catalogId,
                        itemId,
                        lotNumber,
                        title: itemTitle,
                    };

                    subject = formatMessage({ id: 'registration.payment_issue' }, { lotNumber });
                    break;

                default:
                    break;
            }

            dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
        },
        [catalogId, dispatch, formatMessage, itemId, itemTitle, lotNumber, sellerId, sellerName, catalogTitle]
    );
};
